import {
  LivenessWebVerifyStatusEnum,
  RegtankOnboardingStatusEnum,
} from "@funded-here-interface/common/src/constant/enum";
import type {
  RegtankHistoryData,
  RegtankLivenessHistoryData,
} from "../Regtank.types";

/**
 * Utility functions for Regtank component
 * Pure functions that don't depend on React hooks
 */

// Type for regtankFHResultTotal items
interface RegtankFHResult {
  matchRequestId: string;
  newVerificationHistoryStatus: string;
  disabledDocumentBtn?: boolean;
  disabledPhotoVidBtn?: boolean;
  newSubmittedOn?: string;
  newFailureReason?: string;
  newReviewedOn?: string;
  newRemark?: string;
  newReattemptApproval?: string;
}

// Type for livenessFHResultTotal items
interface LivenessFHResult {
  matchSessionId: string;
  newVerificationResult: string;
  disabledPhotoVidBtn?: boolean;
  newSubmittedOn?: string;
  newFailureReason?: string;
  newReviewedOn?: string;
  newRemark?: string;
  newReattemptApproval?: string;
}

/**
 * Gets the verification result for Regtank onboarding history
 */
export const getRegtankHistoryVerificationResult = (
  requestId: string,
  status: RegtankOnboardingStatusEnum,
  regtankFHResultTotal: RegtankFHResult[]
): string | undefined => {
  const matchRegtankFHResult = regtankFHResultTotal.find(
    (x) => x.matchRequestId === requestId
  );
  if (matchRegtankFHResult) {
    if (status === RegtankOnboardingStatusEnum.APPROVED) {
      return "PASSED";
    } else if (matchRegtankFHResult.newVerificationHistoryStatus === "FAILED") {
      return "FAILED";
    } else if (
      matchRegtankFHResult.newVerificationHistoryStatus === "EXPIRED"
    ) {
      return "EXPIRED";
    } else if (matchRegtankFHResult.newVerificationHistoryStatus === "") {
      return "";
    }
  } else {
    if (status === RegtankOnboardingStatusEnum.APPROVED) {
      return "PASSED";
    } else if (
      status === RegtankOnboardingStatusEnum.LIVENESS_FAILED ||
      status === RegtankOnboardingStatusEnum.REJECTED
    ) {
      return "FAILED";
    } else if (status === RegtankOnboardingStatusEnum.EXPIRED) {
      return "EXPIRED";
    }
  }
};

/**
 * Gets the color for Regtank verification result
 */
export const getRegtankVerificationResultColor = (
  requestId: string,
  status: RegtankOnboardingStatusEnum,
  regtankFHResultTotal: RegtankFHResult[]
): string | undefined => {
  const color = getRegtankHistoryVerificationResult(
    requestId,
    status,
    regtankFHResultTotal
  );
  switch (color) {
    case "PASSED":
      return "green";
    case "FAILED":
      return "red";
    case "EXPIRED":
      return "gray";
  }
};

/**
 * Gets the color for reattempt approval status
 */
export const getReattemptApprovalColor = (
  status: string
): string | undefined => {
  const color = status;
  switch (color) {
    case "APPROVED":
      return "green";
    case "REJECTED":
      return "red";
  }
};

/**
 * Checks if document front button should be disabled
 */
export const shouldDisableDocumentFront = (
  requestId: string,
  regtankFHResultTotal: RegtankFHResult[],
  documentIdSubmittedDate: string
): boolean => {
  const hasMatch = regtankFHResultTotal.find(
    (x) => x.matchRequestId === requestId
  );
  if (hasMatch?.disabledDocumentBtn === true || !documentIdSubmittedDate) {
    return true;
  }
  return false;
};

/**
 * Checks if document back button should be disabled
 */
export const shouldDisableDocumentBack = (
  requestId: string,
  regtankFHResultTotal: RegtankFHResult[],
  documentIdSubmittedDate: string
): boolean => {
  const hasMatch = regtankFHResultTotal.find(
    (x) => x.matchRequestId === requestId
  );
  if (hasMatch?.disabledDocumentBtn === true) {
    return true;
  } else if (!documentIdSubmittedDate) {
    return true;
  }
  return false;
};

/**
 * Checks if liveness button should be disabled
 */
export const shouldDisableLiveness = (
  requestId: string,
  regtankFHResultTotal: RegtankFHResult[],
  livenessFHResultTotal: LivenessFHResult[],
  submittedDate: string
): boolean => {
  if (
    regtankFHResultTotal.find((x) => x.matchRequestId === requestId)
      ?.disabledPhotoVidBtn === true ||
    livenessFHResultTotal.find((x) => x.matchSessionId === requestId)
      ?.disabledPhotoVidBtn === true ||
    !submittedDate
  ) {
    return true;
  }

  return false;
};

/**
 * Checks if submission date exists
 */
export const hasSubmittedOn = (
  requestId: string | undefined,
  sessionId: string | undefined,
  regtankFHResultTotal: RegtankFHResult[],
  livenessFHResultTotal: LivenessFHResult[]
): boolean => {
  const match =
    requestId !== ""
      ? regtankFHResultTotal.find((x) => x.matchRequestId === requestId)
      : livenessFHResultTotal.find((x) => x.matchSessionId === sessionId);
  if (match?.newSubmittedOn === "" && match?.newSubmittedOn !== undefined) {
    return false;
  } else {
    return true;
  }
};

/**
 * Gets the new failure reason
 */
export const getNewFailureReason = (
  reason: string,
  requestId: string | undefined,
  sessionId: string | undefined,
  regtankFHResultTotal: RegtankFHResult[],
  livenessFHResultTotal: LivenessFHResult[]
): string[] => {
  let updatedListReason = [];
  const match =
    requestId !== ""
      ? regtankFHResultTotal.find((x) => x.matchRequestId === requestId)
      : livenessFHResultTotal.find((x) => x.matchSessionId === sessionId);
  if (match?.newFailureReason === "" && match?.newFailureReason !== undefined) {
    return [];
  } else if (match && match?.newFailureReason === "-") {
    updatedListReason = match?.newFailureReason.split(",");
  } else {
    updatedListReason = reason.split(",").map((x) => x.trim());
  }
  return updatedListReason;
};

/**
 * Gets the new reviewed on date
 */
export const getNewReviewedOn = (
  reviewedOn: string,
  requestId: string | undefined,
  sessionId: string | undefined,
  regtankFHResultTotal: RegtankFHResult[],
  livenessFHResultTotal: LivenessFHResult[],
  verificationHistoryData: RegtankHistoryData[],
  livenessHistoryData: RegtankLivenessHistoryData[]
): string | undefined => {
  const match = regtankFHResultTotal.find(
    (x) => x.matchRequestId === requestId
  );
  const livenessMatch = livenessFHResultTotal.find(
    (x) => x.matchSessionId === sessionId
  );
  const latestReviewedOn = match
    ? verificationHistoryData.find((x) => x.requestId === match?.matchRequestId)
        ?.reviewedOn
    : livenessHistoryData.find(
        (x) => x.sessionId === livenessMatch?.matchSessionId
      )?.reviewedOn;
  if (latestReviewedOn) {
    return latestReviewedOn;
  }
  if (
    (match?.newReviewedOn === "" && match?.newReviewedOn !== undefined) ||
    (livenessMatch?.newReviewedOn === "" &&
      livenessMatch?.newReviewedOn !== undefined)
  ) {
    return "";
  } else if (
    (match && match?.newReviewedOn === "-") ||
    (livenessMatch && livenessMatch?.newReviewedOn === "-")
  ) {
    return match?.newReviewedOn || livenessMatch?.newReviewedOn;
  } else {
    return reviewedOn;
  }
};

/**
 * Gets the new remark
 */
export const getNewRemark = (
  remark: string,
  requestId: string | undefined,
  sessionId: string | undefined,
  regtankFHResultTotal: RegtankFHResult[],
  livenessFHResultTotal: LivenessFHResult[],
  verificationHistoryData: RegtankHistoryData[],
  livenessHistoryData: RegtankLivenessHistoryData[]
): string | undefined => {
  const match = regtankFHResultTotal.find(
    (x) => x.matchRequestId === requestId
  );
  const livenessMatch = livenessFHResultTotal.find(
    (x) => x.matchSessionId === sessionId
  );
  const latestRemark = match
    ? verificationHistoryData.find((x) => x.requestId === match?.matchRequestId)
        ?.remark
    : livenessHistoryData.find(
        (x) => x.sessionId === livenessMatch?.matchSessionId
      )?.remark;

  if (latestRemark) {
    return latestRemark;
  }
  if (
    (match?.newRemark === "" && match?.newRemark !== undefined) ||
    (livenessMatch?.newRemark === "" && livenessMatch?.newRemark !== undefined)
  ) {
    return "";
  } else if (
    (match && match?.newRemark === "-") ||
    (livenessMatch && livenessMatch?.newRemark === "-")
  ) {
    return match?.newRemark || livenessMatch?.newRemark;
  } else {
    return remark;
  }
};

/**
 * Gets the new reattempt approval status
 */
export const getNewReattemptApproval = (
  reattemptApproval: string,
  requestId: string | undefined,
  sessionId: string | undefined,
  regtankFHResultTotal: RegtankFHResult[],
  livenessFHResultTotal: LivenessFHResult[],
  verificationHistoryData: RegtankHistoryData[],
  livenessHistoryData: RegtankLivenessHistoryData[]
): string | undefined => {
  const match = regtankFHResultTotal.find(
    (x) => x.matchRequestId === requestId
  );
  const livenessMatch = livenessFHResultTotal.find(
    (x) => x.matchSessionId === sessionId
  );
  const latestReattemptApproval = match
    ? verificationHistoryData.find((x) => x.requestId === match?.matchRequestId)
        ?.reattemptApproval
    : livenessHistoryData.find(
        (x) => x.sessionId === livenessMatch?.matchSessionId
      )?.reattemptApproval;
  if (latestReattemptApproval) {
    return latestReattemptApproval;
  }
  if (
    (match?.newReattemptApproval === "" &&
      match?.newReattemptApproval !== undefined) ||
    (livenessMatch?.newReattemptApproval === "" &&
      livenessMatch?.newReattemptApproval !== undefined)
  ) {
    return "";
  } else if (
    (match && match?.newReattemptApproval === "-") ||
    (livenessMatch && livenessMatch?.newReattemptApproval === "-")
  ) {
    return match?.newReattemptApproval || livenessMatch?.newReattemptApproval;
  } else {
    return reattemptApproval;
  }
};

/**
 * Gets the liveness history verification result
 */
export const getLivenessHistoryVerificationResult = (
  sessionId: string,
  status: LivenessWebVerifyStatusEnum,
  livenessFHResultTotal: LivenessFHResult[]
): string | undefined => {
  const matchLivenessFHResult = livenessFHResultTotal.find(
    (x) => x.matchSessionId === sessionId
  );
  if (matchLivenessFHResult) {
    if (matchLivenessFHResult.newVerificationResult === "PASSED") {
      return "PASSED";
    } else if (matchLivenessFHResult.newVerificationResult === "FAILED") {
      return "FAILED";
    } else if (matchLivenessFHResult.newVerificationResult === "") {
      return "";
    }
  } else {
    if (status === LivenessWebVerifyStatusEnum.SUCCESS) {
      return "PASSED";
    } else if (
      status === LivenessWebVerifyStatusEnum.FAILED ||
      status === LivenessWebVerifyStatusEnum.LIVENESS_FAILED
    ) {
      return "FAILED";
    }
  }
};

/**
 * Gets the color for liveness verification result
 */
export const getLivenessVerificationResultColor = (
  sessionId: string,
  status: LivenessWebVerifyStatusEnum,
  livenessFHResultTotal: LivenessFHResult[]
): string | undefined => {
  const color = getLivenessHistoryVerificationResult(
    sessionId,
    status,
    livenessFHResultTotal
  );
  switch (color) {
    case "PASSED":
      return "green";
    case "FAILED":
      return "red";
  }
};
